"use client";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import FlagIcon from "@mui/icons-material/Flag";
import RedFlagPopup from "../../components/dialog/redFlagPopup";
import FloatPopup from "../../components/dialog/floatPopup";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { getSweetAlert } from "../../helper";

export default function WithdrawDialog({ open, onClose, initialUser, initialJoinData, hideRedFlagPopup = false }) {
  const token = useSelector((state) => state.loginSlice.token);

  const [selectedUser, setSelectedUser] = useState(null);
  const [joinData, setJoinData] = useState(null);
  const [lastDeposit, setLastDeposit] = useState(null);
  const [kycDetails, setKycDetails] = useState(null);
  const [earlyWithdrawConfirmed, setEarlyWithdrawConfirmed] = useState(false);

  // Seed joinData from prop when dialog opens
  useEffect(() => { if (open) setJoinData(initialJoinData || null); }, [open, initialJoinData]);

  const [fetching, setFetching] = useState(false);

  const [type, setType]                     = useState("cash");
  const [utr, setUtr]                       = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [bonusHoldback, setBonusHoldback]   = useState("");
  const [holdbackRemark, setHoldbackRemark] = useState("");
  const [remark, setRemark]                 = useState("");
  const [paymentStatus, setPaymentStatus]   = useState("PENDING");
  const [accounts, setAccounts]             = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  useEffect(() => {
    axiosRequest("GET", `${apiBaseUrl}/accounts`, token).then((r) => setAccounts(r?.data?.data || [])).catch(() => {});
  }, [token]);

  /* Fetch fresh user data whenever the dialog opens */
  useEffect(() => {
    if (!open) return;

    if (!initialUser) return;
    const input = initialUser.phone || initialUser.playerId;
    if (!input) { setSelectedUser(initialUser); return; }
    setFetching(true);
    const param = /^\d{10}$/.test(String(input).trim()) ? `phone=${input}` : `playerId=${input}`;
    axiosRequest("GET", `${apiBaseUrl}/user_details?${param}`, token)
      .then((res) => {
        setSelectedUser(res?.data?.userDetails || initialUser);
        setJoinData(res?.data?.userTableJoin || initialJoinData || null);
        setLastDeposit(res?.data?.userLastDeposit || null);
        setKycDetails(res?.data?.kycDetails || null);
      })
      .catch(() => { setSelectedUser(initialUser); setJoinData(initialJoinData || null); setLastDeposit(null); setKycDetails(null); })
      .finally(() => setFetching(false));
  }, [open, initialUser]);

  /* Reset when closed */
  const handleClose = () => {
    setSelectedUser(null);
    setJoinData(null);
    setLastDeposit(null);
    setKycDetails(null);
    setEarlyWithdrawConfirmed(false);
    setType("cash");
    setUtr("");
    setWithdrawAmount("");
    setBonusHoldback("");
    setHoldbackRemark("");
    setRemark("");
    setPaymentStatus("PENDING");
    setSelectedAccountId("");
    onClose();
  };

  /* Derived values */
  const winningBalance  = Number(selectedUser?.winning_balance  || 0);
  const depositBalance  = Number(selectedUser?.cash_deposite    || 0);
  const outstandingLoan = Number(selectedUser?.loan_distributed || 0);
  const withdrawable    = Math.max(0, winningBalance + depositBalance - outstandingLoan);

  const totalWithdraw    = Number(withdrawAmount || 0);
  const totalCash        = totalWithdraw;
  const bonusAmt         = Math.max(0, Number(bonusHoldback || 0));
  const loanDeduction    = Math.min(totalWithdraw, outstandingLoan);
  const netAfterLoan     = totalWithdraw - loanDeduction;
  const netCashToPlayer  = Math.max(0, netAfterLoan - bonusAmt);

  const isOverWithdrawable = totalWithdraw > 0 && totalWithdraw > withdrawable;
  const isBonusExceedsNet  = bonusAmt > 0 && bonusAmt > netAfterLoan;

  // Early withdrawal warning: withdrawing more than last deposit within 5 hours
  const lastDepositAmt  = Number(lastDeposit?.txn_amount || 0);
  const lastDepositTime = lastDeposit?.createdAt ? new Date(lastDeposit.createdAt) : null;
  const hoursSinceDeposit = lastDepositTime ? (Date.now() - lastDepositTime.getTime()) / 36e5 : Infinity;
  const isEarlyWithdraw = totalWithdraw > 0 && lastDepositAmt > 0
    && totalWithdraw > lastDepositAmt
    && hoursSinceDeposit < 5;

  // Reset confirmation whenever the warning condition changes
  // (handled inline via the checkbox — earlyWithdrawConfirmed stays false until cashier checks it)

  /* Submit */
  const handleSubmit = async () => {
    if (totalWithdraw <= 0) { getSweetAlert("error", "Enter withdrawal amount"); return; }
    if (isOverWithdrawable) {
      getSweetAlert("error", `Amount ${totalWithdraw.toLocaleString("en-IN")} exceeds withdrawable balance ${withdrawable.toLocaleString("en-IN")} (Deposit + Winning − Loan)`);
      return;
    }
    if (isBonusExceedsNet) {
      getSweetAlert("error", `Bonus holdback ${bonusAmt.toLocaleString("en-IN")} exceeds net amount ${netAfterLoan.toLocaleString("en-IN")} after loan deduction`);
      return;
    }
    if (bonusAmt > 0 && !holdbackRemark.trim()) {
      getSweetAlert("error", "Remark is required when applying holdback");
      return;
    }
    if (!remark.trim()) { getSweetAlert("error", "Remark is required"); return; }
    if (type === "bank" && !utr) { getSweetAlert("error", "Enter UTR Number"); return; }

    const payload = {
      user_id: selectedUser?._id,
      withdrawType: type,
      totalCash,
      totalChips: totalWithdraw,
      utr,
      bonus_holdback: bonusAmt,
      holdback_remark: bonusAmt > 0 ? holdbackRemark.trim() : undefined,
      remark: remark.trim() || undefined,
      payment_status: paymentStatus,
      account_id: selectedAccountId || undefined,
    };

    const res = await axiosRequest("POST", `${apiBaseUrl}/withdraw`, token, payload);
    if (res?.status || res?.data?.status) {
      getSweetAlert("success", res?.message || res?.data?.message || "Withdraw successful");
      handleClose();
    } else {
      getSweetAlert("error", res?.message || res?.data?.message || "Withdraw failed");
    }
  };

  return (
    <>
    <RedFlagPopup user={open && !fetching ? selectedUser : null} disabled={hideRedFlagPopup} />
    <FloatPopup user={open && !fetching ? selectedUser : null} context="withdraw" disabled={hideRedFlagPopup} />
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {fetching
          ? "Loading player data…"
          : selectedUser
          ? `Withdraw for ${selectedUser.name || selectedUser.user_name || selectedUser.playerId || "Player"}`
          : "Withdraw"}
      </DialogTitle>

      <DialogContent>
        {fetching ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Red flag warning */}
            {selectedUser?.red_flag && (
              <Alert severity="error" icon={<FlagIcon />} sx={{ mb: 2, fontWeight: 600 }}>
                RED FLAG — {selectedUser.red_flag_remark || "This player has been flagged"}
              </Alert>
            )}

            {/* Withdraw lock — blocks submit */}
            {selectedUser?.withdraw_lock && (
              <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 2, fontWeight: 600 }}>
                WITHDRAW LOCKED — this player cannot withdraw. Unlock from Player Manager first.
              </Alert>
            )}

            {/* Over-withdrawable error */}
            {isOverWithdrawable && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Entered amount <strong>{totalWithdraw.toLocaleString("en-IN")}</strong> exceeds withdrawable balance of{" "}
                <strong>{withdrawable.toLocaleString("en-IN")}</strong> (Deposit {depositBalance.toLocaleString("en-IN")} + Winning {winningBalance.toLocaleString("en-IN")} − Loan {outstandingLoan.toLocaleString("en-IN")}).
              </Alert>
            )}

            {/* Loan banner */}
            {!isOverWithdrawable && outstandingLoan > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Outstanding loan: <strong>{outstandingLoan.toLocaleString("en-IN")}</strong>.
                {totalWithdraw > 0 && (
                  <> {loanDeduction.toLocaleString("en-IN")} will be recovered. Player receives <strong>{netCashToPlayer.toLocaleString("en-IN")}</strong>.</>
                )}
              </Alert>
            )}

            <Grid container spacing={3} sx={{ mt: 0 }}>

              {/* Withdraw type */}
              <Grid item xs={12} md={6}>
                <TextField
                  select fullWidth label="Withdraw Type"
                  value={type}
                  onChange={(e) => { setType(e.target.value); setWithdrawAmount(""); }}
                >
                  <MenuItem value="cash">Cash Withdraw</MenuItem>
                  <MenuItem value="bank">Bank Withdraw</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              {/* KYC status + Check-in time */}
              <Grid item xs={12}>
                <Paper sx={{ p: 1.5, background: "#f8f9fa", borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                    <Typography variant="caption" color="text.secondary">KYC Status</Typography>
                    {joinData?.process_time && (
                      <Typography variant="caption" color="text.secondary">
                        Check-in:{" "}
                        <strong>
                          {new Date(joinData.process_time).toLocaleString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                            hour: "2-digit", minute: "2-digit", hour12: true,
                          })}
                        </strong>
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap={1.5} flexWrap="wrap">
                    {[
                      { label: "Aadhaar", field: "is_aadhar_verified" },
                      { label: "Mobile",  field: "is_mobile_verified" },
                      { label: "Email",   field: "is_email_verified" },
                      { label: "Bank",    field: "is_bank_verified" },
                    ].map(({ label, field }) => {
                      const verified = selectedUser?.[field];
                      return (
                        <Chip
                          key={field}
                          size="small"
                          label={`${label}: ${verified ? "✓" : "✗"}`}
                          color={verified ? "success" : "error"}
                          variant={verified ? "filled" : "outlined"}
                        />
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>

              {/* Bank details — only when bank is verified */}
              {kycDetails?.is_bank_verified && kycDetails?.bank_details?.account_number && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 1.5, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 2 }}>
                    <Typography variant="caption" color="success.dark" fontWeight={700} display="block" mb={1}>
                      ✓ Verified Bank Account
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Account Holder</Typography>
                        <Typography variant="body2" fontWeight={700}>{kycDetails.bank_details.account_holder_name || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Account Number</Typography>
                        <Typography variant="body2" fontWeight={700}>{kycDetails.bank_details.account_number}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">IFSC</Typography>
                        <Typography variant="body2" fontWeight={700}>{kycDetails.bank_details.ifsc_code || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Bank</Typography>
                        <Typography variant="body2" fontWeight={700}>{kycDetails.bank_details.bank_name || "—"}</Typography>
                      </Box>
                      {kycDetails.bank_details.branch && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Branch</Typography>
                          <Typography variant="body2" fontWeight={700}>{kycDetails.bank_details.branch}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Balance summary */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 1.5, background: "#f8f9fa", borderRadius: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={2.4} textAlign="center">
                      <Typography variant="caption" color="primary">Deposit</Typography>
                      <Typography fontWeight={700} color="primary.main">{depositBalance.toLocaleString("en-IN")}</Typography>
                    </Grid>
                    <Grid item xs={2.4} textAlign="center">
                      <Typography variant="caption" color="text.secondary">Winning</Typography>
                      <Typography fontWeight={700}>{winningBalance.toLocaleString("en-IN")}</Typography>
                    </Grid>
                    <Grid item xs={2.4} textAlign="center">
                      <Typography variant="caption" color="secondary">Promo</Typography>
                      <Typography fontWeight={700} color="secondary.main">{Number(selectedUser?.promotional_balance || 0).toLocaleString("en-IN")}</Typography>
                    </Grid>
                    <Grid item xs={2.4} textAlign="center">
                      <Typography variant="caption" color="error">Loan</Typography>
                      <Typography fontWeight={700} color="error.main">− {outstandingLoan.toLocaleString("en-IN")}</Typography>
                    </Grid>
                    <Grid item xs={2.4} textAlign="center">
                      <Typography variant="caption" color="success.main">Withdrawable</Typography>
                      <Typography fontWeight={700} color="success.main">{withdrawable.toLocaleString("en-IN")}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Withdrawal amount */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" label="Withdrawal Amount ()"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  inputProps={{ min: 0 }}
                  error={isOverWithdrawable}
                  helperText={isOverWithdrawable
                    ? `Max withdrawable: ${withdrawable.toLocaleString("en-IN")}`
                    : `Withdrawable: ${withdrawable.toLocaleString("en-IN")}`}
                />
              </Grid>

              {/* Bonus Holdback (optional) */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" label="Bonus Hold-back () — Optional"
                  value={bonusHoldback}
                  onChange={(e) => setBonusHoldback(e.target.value)}
                  inputProps={{ min: 0 }}
                  error={isBonusExceedsNet}
                  helperText={isBonusExceedsNet
                    ? `Cannot exceed net ${netAfterLoan.toLocaleString("en-IN")} after loan`
                    : bonusAmt > 0
                    ? `${bonusAmt.toLocaleString("en-IN")} held as penalty — stays at counter`
                    : "Penalty amount deducted from player, stays at counter"}
                />
              </Grid>

              {/* Holdback remark — only when holdback is applied */}
              {bonusAmt > 0 && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Holdback Remark (required)"
                    placeholder="Reason for holdback penalty"
                    value={holdbackRemark}
                    onChange={(e) => setHoldbackRemark(e.target.value)}
                    error={bonusAmt > 0 && !holdbackRemark.trim()}
                    helperText={bonusAmt > 0 && !holdbackRemark.trim() ? "Remark is required when applying holdback" : ""}
                  />
                </Grid>
              )}

              {/* UTR for bank */}
              {type === "bank" && (
                <Grid item xs={12}>
                  <TextField fullWidth label="UTR Number" value={utr} onChange={(e) => setUtr(e.target.value)} />
                </Grid>
              )}

              {/* Remark — all types */}
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Remark"
                  placeholder="Add a note for this withdrawal"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  required
                  error={!remark.trim()}
                  helperText={!remark.trim() ? "Remark is required" : ""}
                />
              </Grid>

              {/* Early withdrawal warning */}
              {isEarlyWithdraw && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    sx={{ border: "2px solid #f59e0b", borderRadius: 2 }}
                  >
                    <Typography fontWeight={700} mb={0.5}>
                      Early Withdrawal Warning
                    </Typography>
                    <Typography variant="body2">
                      Player is withdrawing{" "}
                      <strong>{totalWithdraw.toLocaleString("en-IN")}</strong> which exceeds
                      their last deposit of{" "}
                      <strong>{lastDepositAmt.toLocaleString("en-IN")}</strong> made only{" "}
                      <strong>
                        {Math.floor(hoursSinceDeposit) > 0
                          ? `${Math.floor(hoursSinceDeposit)}h ${Math.round((hoursSinceDeposit % 1) * 60)}m`
                          : `${Math.round(hoursSinceDeposit * 60)} min`}
                      </strong> ago (within 5-hour window).
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Checkbox
                          checked={earlyWithdrawConfirmed}
                          onChange={(e) => setEarlyWithdrawConfirmed(e.target.checked)}
                          color="warning"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={600} color="warning.dark">
                          I acknowledge and allow this withdrawal
                        </Typography>
                      }
                    />
                  </Alert>
                </Grid>
              )}

              {/* Summary */}
              {totalWithdraw > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{
                    p: 2, borderRadius: 2,
                    background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                    color: "#fff",
                  }}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={3} textAlign="center">
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Withdraw Amount</Typography>
                        <Typography variant="h6" fontWeight={700}>{totalWithdraw.toLocaleString("en-IN")}</Typography>
                      </Grid>
                      <Grid item xs={3} textAlign="center">
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Loan Recovered</Typography>
                        <Typography variant="h6" fontWeight={700} sx={{ color: loanDeduction > 0 ? "#ff6b6b" : "rgba(255,255,255,0.4)" }}>
                          {loanDeduction > 0 ? `− ${loanDeduction.toLocaleString("en-IN")}` : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} textAlign="center">
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Bonus Held</Typography>
                        <Typography variant="h6" fontWeight={700} sx={{ color: bonusAmt > 0 ? "#ffd54f" : "rgba(255,255,255,0.4)" }}>
                          {bonusAmt > 0 ? `− ${bonusAmt.toLocaleString("en-IN")}` : "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} textAlign="center">
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Cash to Player</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{ color: "#38ef7d" }}>{netCashToPlayer.toLocaleString("en-IN")}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

            </Grid>
          </>
        )}
      </DialogContent>

      <Divider />

      {/* Account Ledger picker — only shown when marking payment as paid */}
      {accounts.length > 0 && paymentStatus === "SUCCESS" && (
        <Box sx={{ px: 3, pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Link to Account (optional)</InputLabel>
            <Select value={selectedAccountId} label="Link to Account (optional)" onChange={(e) => setSelectedAccountId(e.target.value)}>
              <MenuItem value="">— None —</MenuItem>
              {accounts.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}{a.phone ? ` — ${a.phone}` : ""}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      )}

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Chip label={`Mode: ${type === "cash" ? "Cash" : type === "bank" ? "Bank" : "Other"}`} color="primary" />
        <TextField
          select size="small" label="Payment Status"
          value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="SUCCESS">Success</MenuItem>
        </TextField>
        <Box sx={{ ml: "auto", display: "flex", gap: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={fetching || !!selectedUser?.withdraw_lock || totalWithdraw <= 0 || isOverWithdrawable || isBonusExceedsNet || (isEarlyWithdraw && !earlyWithdrawConfirmed) || (bonusAmt > 0 && !holdbackRemark.trim()) || !remark.trim()}
          >
            Confirm Withdraw
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    </>
  );
}
